import React, { useEffect } from 'react';

import type { IntakeFormFileValue, UUID } from '@aline/common';
import { CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { PanelHeader } from 'src/components/generic';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { Apollo } from 'src/gql/generated';
import type { Apollo as ApolloTypes } from 'src/gql/generated';
import { getWorkflowWasSentForSig } from 'src/workflow/helpers';
import { makeStyles } from 'tss-react/mui';

import {
  draftFieldsFromDefinition,
  INTAKE_FORM_TITLE_INPUT_PLACEHOLDER,
  INTAKE_FORM_TITLE_TEXT_PLACEHOLDER,
} from '../intake-form/intake-form-fields';
import { IntakeFormFields } from '../intake-form/IntakeFormFields';
import { useIntakeFormValues } from '../intake-form/use-intake-form-values';
import { useWorkflowDetailLinks } from '../WorkflowDetailContext';

type FlushPendingIntakeValues = () => Promise<void>;

const useStyles = makeStyles()(theme => ({
  actions: {
    color: theme.appColors.iconsType,
    fontSize: 16,
    fontWeight: 600,
    marginBottom: theme.spacing(2),
  },
  centered: {
    alignItems: 'center',
    color: theme.appColors.iconsType,
    display: 'flex',
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  content: {
    /**
     * Paint containment is required (overflow alone is not enough): the intake
     * form is a CSS grid, and inside this nested flex/grid drawer chain its
     * grid content propagates its scrollable overflow up to the document,
     * growing the page instead of scrolling within the panel. `contain: paint`
     * isolates that overflow so the form scrolls inside this container.
     */
    contain: 'paint',
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  titleDisplay: {
    color: theme.appColors.iconsType,
    fontSize: 22,
    fontWeight: 'bold',
  },
  titleInput: {
    color: theme.appColors.iconsType,
    fontSize: 22,
    fontWeight: 'bold',
    padding: 0,
  },
  titleLabel: {
    color: theme.appColors.iconsType,
    fontSize: 13,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
  },
  titleSection: {
    marginBottom: theme.spacing(2),
  },
}));

export function IntakeFormSidePanel({ workflowId }: { workflowId: UUID }) {
  const navigate = useNavigate();
  const { classes } = useStyles();
  const links = useWorkflowDetailLinks(workflowId);
  const flushPendingValuesRef = React.useRef<FlushPendingIntakeValues | null>(null);
  const registerFlushPendingValues = React.useCallback(
    (flushPendingValues: FlushPendingIntakeValues | null) => {
      flushPendingValuesRef.current = flushPendingValues;
    },
    []
  );
  const handleBack = React.useCallback(async () => {
    await flushPendingValuesRef.current?.();
    navigate(links.baseUrl());
  }, [links, navigate]);
  const { data, loading } = Apollo.useWorkflowIntakeFormQuery({ variables: { workflowId } });
  const intakeForm = data?.workflow?.intakeForm ?? null;
  const removalLocked = getWorkflowWasSentForSig(data?.workflow ?? null);

  return (
    <div className={classes.root}>
      <PanelHeader
        showBackButton
        backButtonTooltip="Back to AI"
        text="Intake information"
        onClickBack={handleBack}
      />
      {intakeForm ? (
        <IntakeFormPanelBody
          key={intakeForm.id}
          intakeForm={intakeForm}
          registerFlushPendingValues={registerFlushPendingValues}
          removalLocked={removalLocked}
          workflowId={workflowId}
        />
      ) : (
        <div className={classes.centered}>
          {loading ? <CircularProgress size={24} /> : 'No intake form on this workflow.'}
        </div>
      )}
    </div>
  );
}

function IntakeFormPanelBody({
  intakeForm,
  registerFlushPendingValues,
  removalLocked,
  workflowId,
}: {
  intakeForm: ApolloTypes.IntakeFormFullFragment;
  registerFlushPendingValues: (flushPendingValues: FlushPendingIntakeValues | null) => void;
  removalLocked: boolean;
  workflowId: UUID;
}) {
  const { classes } = useStyles();
  const fields = draftFieldsFromDefinition(intakeForm.formDefinition);
  const [updateIntakeForm] = Apollo.useUpdateIntakeFormValuesMutation();
  const [removeDocumentsFromWorkflow] = Apollo.useRemoveDocumentsFromWorkflowMutation();
  const { flushPendingValues, setFieldValue, values } = useIntakeFormValues(workflowId, intakeForm);
  const removeWorkflowFile = React.useCallback(
    async (file: IntakeFormFileValue) => {
      await removeDocumentsFromWorkflow({
        variables: { input: { documentIds: [file.documentId], workflowId } },
      });
    },
    [removeDocumentsFromWorkflow, workflowId]
  );

  useEffect(() => {
    registerFlushPendingValues(flushPendingValues);
    return () => registerFlushPendingValues(null);
  }, [flushPendingValues, registerFlushPendingValues]);

  return (
    <div className={classes.content}>
      <div className={classes.titleSection}>
        <div className={classes.titleLabel}>Title</div>
        <InlineEditField
          classes={{ display: classes.titleDisplay, input: classes.titleInput }}
          placeholder={INTAKE_FORM_TITLE_INPUT_PLACEHOLDER}
          textPlaceholder={INTAKE_FORM_TITLE_TEXT_PLACEHOLDER}
          value={intakeForm.title}
          onSave={title => {
            updateIntakeForm({ variables: { input: { title, workflowId } } });
          }}
        />
      </div>
      <div className={classes.actions}>Actions</div>
      <IntakeFormFields
        disabled={removalLocked}
        fields={fields}
        formDefinition={intakeForm.formDefinition}
        missingFileDocumentIds={intakeForm.missingFileDocumentIds}
        removalLocked={removalLocked}
        values={values}
        onChange={setFieldValue}
        onRemoveFile={removeWorkflowFile}
      />
    </div>
  );
}
